import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useContextSelector } from 'use-context-selector'
import { useToaster } from 'ui-seeker/core'
import AppReportExecutionContext from 'app/providers/@contexts/reportExecution'

// Historical report executions (older than the ClickHouse retention) are read from BigQuery and load slower.
// The backend flags each option of `selector/id-report-execution` with `historical` / `historical_months`.
// Shows the toaster ONCE per idReportExecution, when it is selected or when the page arrives with it in the URL
// (initial options load), and publishes the flag in AppReportExecutionContext for the header badge.
export default function useHistoricalExecutionNotice(value) {
    const { t } = useTranslation('common')
    const { showToaster } = useToaster()
    const setReportExecution = useContextSelector(AppReportExecutionContext, ({ updateState }) => updateState)
    const notifiedRef = useRef(null)

    // leaving the Analyzer (header unmounted) clears the badge
    useEffect(() => () => setReportExecution({ isHistorical: false }), [setReportExecution])

    const notify = useCallback(option => {
        const historical = !!option?.historical
        setReportExecution({ isHistorical: historical })
        if (!historical || notifiedRef.current === option.value) return
        notifiedRef.current = option.value
        showToaster.info(t('reportExecution.historical.notice', { count: option.historical_months || 1 }), { icon: null })
    }, [showToaster, t, setReportExecution])

    const onChange = useCallback(option => {
        if (!option) {
            notifiedRef.current = null
            setReportExecution({ isHistorical: false })
            return
        }
        notify(option)
    }, [notify, setReportExecution])

    const onAjax = useCallback((options = []) => {
        const selected = value ? options.find(o => String(o.value) === String(value)) : null
        if (selected) notify(selected)
        return options
    }, [value, notify])

    return { onChange, onAjax }
}
